import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { calendarWeeks } from "@/lib/dashboard";
import { cn } from "@/lib/utils";

const LEVELS = [
  "bg-muted",
  "bg-tile-mint-strong/30",
  "bg-tile-mint-strong/55",
  "bg-tile-mint-strong/80",
  "bg-tile-mint-strong",
];

/**
 * Bir yillik faollik kalendari (GitHub uslubi): har katak — bir kun,
 * rang — o'sha kungi amallar soni. Katakni bosish o'sha kunning
 * statistikasini ochadi.
 */
export function ActivityCalendar({
  entries,
  today,
  onPick,
}: {
  entries: { date: string; count: number }[];
  today: string;
  onPick?: (date: string) => void;
}) {
  const { t } = useTranslation();
  const weeks = calendarWeeks(entries, today);
  const total = entries.reduce((sum, entry) => sum + entry.count, 0);
  const days = entries.filter(entry => entry.count > 0).length;

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        {t("userStats.calendarSummary", { days, total })}
      </p>
      <div className="thin-scrollbar overflow-x-auto pb-1">
        <div className="flex w-max gap-[3px]" role="grid" aria-label={t("userStats.calendar")}>
          {weeks.map((week, weekIndex) => (
            <div key={week[0]?.date} role="row" className="flex flex-col gap-[3px]">
              {week.map(cell => (
                <motion.button
                  key={cell.date}
                  type="button"
                  role="gridcell"
                  disabled={cell.future}
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: weekIndex * 0.012 }}
                  title={t("userStats.calendarCell", { date: cell.date, count: cell.count })}
                  aria-label={t("userStats.calendarCell", { date: cell.date, count: cell.count })}
                  onClick={() => onPick?.(cell.date)}
                  className={cn(
                    "size-3 rounded-[3px] transition-transform hover:scale-150 disabled:invisible",
                    LEVELS[cell.level],
                    cell.date === today && "ring-primary ring-offset-card ring-1 ring-offset-1",
                  )}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="text-muted-foreground flex items-center justify-end gap-1 text-[11px]">
        {t("userStats.less")}
        {LEVELS.map(level => (
          <span key={level} className={cn("size-3 rounded-[3px]", level)} />
        ))}
        {t("userStats.more")}
      </div>
    </div>
  );
}
